/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { Specialization_ProductVariant as S12nProductVariantValues } from '__generated__/graphql-types';

import { processImage } from 'js/lib/imgix';
import type UserAgent from 'js/lib/useragent';

import { breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { BrowseCarousel } from 'bundles/browse-common/legacy/components/BrowseCarousel';
import { SCREEN_TABLET } from 'bundles/browse/constants';
import {
  buildCertificatesUrlAbsolute,
  buildCourseUrlAbsolute,
  buildMastertrackUrlAbsolute,
  buildProfessionalCertificateUrlAbsolute,
  buildS12nsUrlAbsolute,
} from 'bundles/common/utils/urlUtils';
import type { UpsellProductVariant } from 'bundles/degree-description/types';
import { filterNullOrUndefined, productVariantToEventing } from 'bundles/degree-description/utils/utils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import ProductCard from 'bundles/xddp/components/shared/ProductCard';
import type { UpsellsByDegreeSlug } from 'bundles/xddp/types';
import { getProductDisplayProps, getUpsellInfo } from 'bundles/xddp/utils/helpers';

import CarouselFallBackImage from 'images/xdp/two-women-standing-and-smiling-at-tablet.jpg';

const responsiveProperty = [
  {
    breakpoint: 608,
    settings: { slidesToShow: 1, slidesToScroll: 1, variableWidth: true, arrows: false },
  },
  { breakpoint: 824, settings: { slidesToShow: 2, slidesToScroll: 2, arrows: true } },
];

const styles: Record<string, Interpolation<Theme>> = {
  productCard: () => css`
    height: 374px;
    padding: 0 8px;
    ${breakpoints.down(608)} {
      width: 300px !important;
    }
  `,
  carousel: css`
    & .slick-next {
      right: -54px !important;
    }

    & .slick-prev {
      left: -54px !important;
    }
  `,
};

type Props = {
  items: UpsellsByDegreeSlug;
  userAgent: UserAgent;
};

const StackableItemsCarousel: React.FC<Props> = ({ items, userAgent }) => {
  const track = useTracker();
  if (!items) {
    return null;
  }

  const itemLength = items.length;

  const buildS12nUrlFromProductVariant = (variant: UpsellProductVariant, slug: string) => {
    switch (variant) {
      case S12nProductVariantValues.NormalS12N:
        return buildS12nsUrlAbsolute(slug);
      case S12nProductVariantValues.ProfessionalCertificateS12N:
        return buildProfessionalCertificateUrlAbsolute(slug);
      case 'MasterTrack':
        return buildMastertrackUrlAbsolute(slug);
      case 'standardCourse':
        return buildCourseUrlAbsolute(slug);
      case 'UniversityCertificate':
        return buildCertificatesUrlAbsolute(slug);
      default:
        return null;
    }
  };

  return (
    <div css={styles.carousel}>
      <Media
        defaultMatches={userAgent && userAgent.isMobileBrowser}
        query={{ maxWidth: SCREEN_TABLET }}
        data-testid="stackability-carousel"
      >
        {() => (
          <BrowseCarousel
            slidesToShow={2}
            slidesToScroll={2}
            responsive={responsiveProperty}
            showArrows={Boolean(itemLength > 2)}
            showDots={false}
            style={{ paddingLeft: '12px' }}
          >
            {items
              .map((item, index) => {
                const upsellInfo = getUpsellInfo([item]);
                const trackingProductVariant =
                  upsellInfo?.upsell.productVariant && productVariantToEventing(upsellInfo.upsell.productVariant);

                const handleOnClick = () => {
                  if (upsellInfo?.upsell) {
                    const {
                      upsell: { id, name, slug },
                      upsellPartners,
                    } = upsellInfo;
                    track('click_product_card', {
                      productCard: {
                        index,
                      },
                      product: {
                        id,
                        ...(trackingProductVariant && {
                          type: trackingProductVariant,
                        }),
                        ...(name && {
                          name,
                        }),
                        ...(upsellPartners &&
                          upsellPartners?.[0] && {
                            associatedPartner: {
                              primaryPartnerId: upsellPartners[0].id,
                              ...(upsellPartners?.[1] && {
                                secondaryPartnerId: upsellPartners[1].id,
                              }),
                            },
                          }),
                        ...(slug && {
                          slug,
                        }),
                      },
                      pageSection: {
                        sectionName: 'upsell',
                      },
                    });
                  }
                };

                if (upsellInfo) {
                  const linkUrl = buildS12nUrlFromProductVariant(
                    upsellInfo.upsell.productVariant as UpsellProductVariant,
                    upsellInfo.upsell.slug || ''
                  );
                  const { gradient, label } = getProductDisplayProps(
                    upsellInfo.upsell.productVariant as UpsellProductVariant,
                    upsellInfo.courseIds
                  );
                  return (
                    <div css={styles.productCard} key={upsellInfo.upsell.id}>
                      <TrackedLink2
                        style={{ textDecoration: 'none' }}
                        role="link"
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        trackingName={`${upsellInfo.upsell.productVariant?.toLowerCase()}_upsell_card`}
                        onClick={handleOnClick}
                        data={{
                          slug: upsellInfo.upsell.slug,
                          title: upsellInfo.upsell.name,
                          partners: upsellInfo.upsellPartners,
                          productVariant: upsellInfo.upsell.productVariant,
                          position: items.indexOf(item),
                        }}
                      >
                        <ProductCard
                          title={upsellInfo.upsell.name || ''}
                          subtitle={upsellInfo.upsellPartners[0]?.name}
                          imageSrc={processImage(upsellInfo.headerImage || CarouselFallBackImage)}
                          logoSrc={processImage(upsellInfo.logo)}
                          gradient={gradient}
                          label={label}
                        />
                      </TrackedLink2>
                    </div>
                  );
                } else {
                  return null;
                }
              })
              .filter(filterNullOrUndefined)}
          </BrowseCarousel>
        )}
      </Media>
    </div>
  );
};

export default StackableItemsCarousel;
